import { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { cmsApi } from '../lib/api/cms';
import ServiceDetail from './ServiceDetail';

const ServiceSection = () => {
  const { t, getTranslation } = useTranslation();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const [services, setServices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Load services from CMS
  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    try {
      const { data: cmsServices, error } = await cmsApi.getServices();
      
      if (error) {
        console.error('Error loading services:', error);
        // Fall back to default services if CMS fails
        setServices(getDefaultServices());
        return;
      }

      // Get services from translations
      const servicesData = getTranslation('services.items') || {};
      
      // Merge CMS data with translation data
      const mergedServices = Object.keys(servicesData).map(key => {
        const cmsService = cmsServices?.find(s => s.id === key);
        const translationData = servicesData[key];
        
        return {
          id: key,
          title: translationData.title || cmsService?.title || key,
          description: translationData.description || cmsService?.short_description || '',
          icon: getServiceIcon(key),
          // Use CMS image if available, otherwise fall back to default
          image: cmsService?.image_url || getDefaultServiceImage(key)
        };
      });

      setServices(mergedServices);
    } catch (err) {
      console.error('Error loading services:', err);
      setServices(getDefaultServices());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultServices = () => {
    const servicesData = getTranslation('services.items') || {};
    
    return Object.keys(servicesData).map(key => ({
      id: key,
      title: servicesData[key].title,
      description: servicesData[key].description,
      icon: getServiceIcon(key),
      image: getDefaultServiceImage(key)
    }));
  };

  function getServiceIcon(serviceId: string): string {
    const icons: Record<string, string> = {
      'raieoiguse-ost': 'M12 2L14.5 5H9.5L12 2zM7 6H17V8H7V6zM5 9H19L20 19H4L5 9zM9 11V16H7V11H9zM11 11V16H13V11H11zM15 11V16H17V11H15z',
      'metsamaterjali-ost': 'M7 2V5H3V7H21V5H17V2H7zM5 8V20H7V8H5zM9 8V20H11V8H9zM13 8V20H15V8H13zM17 8V20H19V8H17z',
      'hakkepuidu-ost-muuk': 'M15 3L12 6L9 3L3 9L6 12L3 15L9 21L12 18L15 21L21 15L18 12L21 9L15 3zM12 8L16 12L12 16L8 12L12 8z',
      'kuttepuude-muuk': 'M12 2L14 6H10L12 2zM7 7H17L16 10H8L7 7zM6 11H18L17 14H7L6 11zM5 15H19L18 18H6L5 15zM4 19H20L19 22H5L4 19z',
      'metsamaa-ost-muuk': 'M12 2L15 5L12 8L9 5L12 2zM4 9H20V11L18 13H6L4 11V9zM3 14H21V17L19 19H5L3 17V14zM2 20H22V22H2V20z',
      'metsamaterjali-ulestootamine': 'M8 2V4H4V6H20V4H16V2H8zM6 7V10L3 14V17H7C7 18.7 8.3 20 10 20C11.7 20 13 18.7 13 17H21V14L18 10V7H6zM8 9H16V10L18 13H6L8 10V9zM10 15C10.6 15 11 15.4 11 16C11 16.6 10.6 17 10 17C9.4 17 9 16.6 9 16C9 15.4 9.4 15 10 15z',
      'metsa-istutamine': 'M12 2L14 4L12 6L10 4L12 2zM12 7L14 9L12 11L10 9L12 7zM12 12L14 14L12 16L10 14L12 12zM3 17H21V22H3V17z',
      'metsamaterjali-transport': 'M4 6H18V8L20 8V10L4 10V6zM3 11H21L23 15V17H21C21 18.7 19.7 20 18 20C16.3 20 15 18.7 15 17H9C9 18.7 7.7 20 6 20C4.3 20 3 18.7 3 17H1V13L3 11zM6 15.5C6.8 15.5 7.5 16.2 7.5 17C7.5 17.8 6.8 18.5 6 18.5C5.2 18.5 4.5 17.8 4.5 17C4.5 16.2 5.2 15.5 6 15.5zM18 15.5C18.8 15.5 19.5 16.2 19.5 17C19.5 17.8 18.8 18.5 18 18.5C17.2 18.5 16.5 17.8 16.5 17C16.5 16.2 17.2 15.5 18 15.5z'
    };
    return icons[serviceId] || '';
  }

  function getDefaultServiceImage(serviceId: string): string {
    const images: Record<string, string> = {
      'raieoiguse-ost': 'https://images.pexels.com/photos/2480232/pexels-photo-2480232.jpeg?auto=compress&cs=tinysrgb&w=800',
      'metsamaterjali-ost': 'https://images.pexels.com/photos/6868757/pexels-photo-6868757.jpeg?auto=compress&cs=tinysrgb&w=800',
      'hakkepuidu-ost-muuk': 'https://images.pexels.com/photos/2763886/pexels-photo-2763886.jpeg?auto=compress&cs=tinysrgb&w=800',
      'kuttepuude-muuk': 'https://images.pexels.com/photos/6205509/pexels-photo-6205509.jpeg?auto=compress&cs=tinysrgb&w=800',
      'metsamaa-ost-muuk': 'https://images.pexels.com/photos/5758391/pexels-photo-5758391.jpeg?auto=compress&cs=tinysrgb&w=800',
      'metsamaterjali-ulestootamine': 'https://images.pexels.com/photos/1112186/pexels-photo-1112186.jpeg?auto=compress&cs=tinysrgb&w=800',
      'metsa-istutamine': 'https://images.pexels.com/photos/1108572/pexels-photo-1108572.jpeg?auto=compress&cs=tinysrgb&w=800',
      'metsamaterjali-transport': 'https://images.pexels.com/photos/12670088/pexels-photo-12670088.jpeg?auto=compress&cs=tinysrgb&w=800'
    };
    return images[serviceId] || '';
  }

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });

    document.querySelectorAll('#services .grid > div').forEach((card) => {
      observer.observe(card);
    });

    return () => observer.disconnect();
  }, [services]);

  // Listen for CMS updates
  useEffect(() => {
    const handleCMSUpdate = () => {
      loadServices();
    };

    window.addEventListener('translationsUpdated', handleCMSUpdate);
    return () => {
      window.removeEventListener('translationsUpdated', handleCMSUpdate);
    };
  }, []);

  if (loading) {
    return (
      <section id="services\" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-700"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t('services.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {services.map(service => (
            <div
              key={service.id}
              id={service.id}
              className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg group flex flex-col"
            >
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={service.image} 
                  alt={service.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  onError={(e) => {
                    // Fallback to default image if CMS image fails to load
                    const target = e.target as HTMLImageElement;
                    target.src = getDefaultServiceImage(service.id);
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-900/70 to-transparent opacity-80"></div>
                <div className="absolute bottom-0 left-0 p-4">
                  <h3 className="text-xl font-bold text-white">{service.title}</h3>
                </div>
              </div>
              <div className="p-6 flex-grow flex flex-col">
                <div className="flex items-start mb-4 flex-grow">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center bg-primary-100 rounded-full mr-4">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 text-primary-700" fill="currentColor">
                      <path d={service.icon}></path>
                    </svg>
                  </div>
                  <p className="text-gray-600">{service.description}</p>
                </div>
                <div className="mt-auto">
                  <button
                    onClick={() => setSelectedService(service.id)}
                    className="inline-flex items-center text-primary-700 font-medium hover:text-primary-800 transition-colors"
                  >
                    {t('services.readMore')}
                    <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {services.map(service => (
        <ServiceDetail
          key={service.id}
          service={service}
          isOpen={selectedService === service.id}
          onClose={() => setSelectedService(null)}
        />
      ))}
    </section>
  );
};

export default ServiceSection;